import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';

export type Theme = 'red' | 'green' | 'yellow' | 'blue';

const PORTAL_THEMES: Record<string, Theme> = {
  learner: 'yellow',
  'driving-school': 'blue',
  instructor: 'blue',
  'driving-institute': 'red',
  vis: 'red',
  'vis-instructor': 'red',
  'vis-examiner': 'red',
};

const THEME_STORAGE_KEY = 'driveright.theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private currentPortal: string | null = null;
  readonly activeTheme = signal<Theme>('yellow');

  constructor() {
    const location = this.document.defaultView?.location;
    this.applyRouteTheme(location?.hash.startsWith('#/') ? location.hash.slice(1) : location?.pathname ?? '/');
  }

  applyRouteTheme(url: string): void {
    this.currentPortal = this.portalFor(url);
    const theme = this.currentPortal
      ? this.readTheme(`${THEME_STORAGE_KEY}.${this.currentPortal}`) ?? PORTAL_THEMES[this.currentPortal]
      : this.readTheme(THEME_STORAGE_KEY) ?? this.activeTheme();
    this.setTheme(theme);
  }

  setTheme(theme: Theme, destinationUrl?: string): void {
    if (destinationUrl !== undefined) this.currentPortal = this.portalFor(destinationUrl);
    this.activeTheme.set(theme);
    this.document.body.setAttribute('data-theme', theme);
    try {
      const storage = this.document.defaultView?.localStorage;
      storage?.setItem(THEME_STORAGE_KEY, theme);
      if (this.currentPortal) storage?.setItem(`${THEME_STORAGE_KEY}.${this.currentPortal}`, theme);
    } catch {
      // The selected theme still applies when browser storage is unavailable.
    }
  }

  private portalFor(url: string): string | null {
    const portal = url.split(/[?#]/)[0].split('/').filter(Boolean)[0];
    return Object.prototype.hasOwnProperty.call(PORTAL_THEMES, portal) ? portal : null;
  }

  private readTheme(key: string): Theme | null {
    try {
      const theme = this.document.defaultView?.localStorage.getItem(key);
      return theme === 'red' || theme === 'green' || theme === 'yellow' || theme === 'blue' ? theme : null;
    } catch {
      return null;
    }
  }
}
